import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react'
import type { ForwardRefRenderFunction } from 'react'
import {
  createFirework,
  createRandomFirework,
  createEffectParticles,
} from '@/utils/fireworks'

interface FireworkSettings {
  theme?: string
  autoLaunch?: boolean
  maxFireworks?: number
  particleDensity?: number
  transparent?: boolean
}

interface FireworkCanvasProps {
  settings?: FireworkSettings
  autoLaunch?: boolean
}

interface FireworkCanvasRef {
  canvas: HTMLCanvasElement | null
}

const FireworkCanvasComponent: ForwardRefRenderFunction<
  FireworkCanvasRef,
  FireworkCanvasProps
> = ({ settings, autoLaunch = true }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number | undefined>(undefined)
  const fireworksRef = useRef<any[]>([])
  const particlesRef = useRef<any[]>([])
  const autoFireworkIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const [isVisible, setIsVisible] = useState(true)
  const [selectedTheme, setSelectedTheme] = useState(
    settings?.theme || 'default',
  )
  const maxFireworks = settings?.maxFireworks || 5
  const particleDensity = settings?.particleDensity || 100
  const isTransparent = settings?.transparent ?? false

  // Cyberpunk color themes
  const themes = {
    neonTokyo: ['#ff00ff', '#00ffff', '#ff3377', '#3377ff', '#ff00aa'],
    digitalWasteland: ['#00ff66', '#ff9900', '#66ff00', '#cc00ff', '#00ffaa'],
    corpTech: ['#0066ff', '#ff0066', '#00ccff', '#ffcc00', '#00ffcc'],
    retroWave: ['#ff00aa', '#aa00ff', '#00aaff', '#ffaa00', '#ff5500'],
  }

  const selectedColors =
    themes[selectedTheme as keyof typeof themes] || themes.neonTokyo

  useImperativeHandle(ref, () => ({
    canvas: canvasRef.current,
  }))

  const createInitialFireworks = useCallback(() => {
    if (!canvasRef.current) return
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        if (!canvasRef.current) return
        fireworksRef.current.push(
          createRandomFirework(canvasRef.current, selectedColors),
        )
      }, i * 300)
    }
  }, [selectedColors])

  const startAutoFireworks = useCallback(() => {
    if (autoFireworkIntervalRef.current) {
      clearInterval(autoFireworkIntervalRef.current)
    }

    autoFireworkIntervalRef.current = setInterval(
      () => {
        if (fireworksRef.current.length < maxFireworks && canvasRef.current) {
          fireworksRef.current.push(
            createRandomFirework(canvasRef.current, selectedColors),
          )
        }
      },
      1500 + Math.random() * 2000,
    )
  }, [selectedColors, maxFireworks])

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible')
      if (document.visibilityState === 'visible') {
        // 当页面重新可见时，清空现有的烟花和粒子
        fireworksRef.current = []
        particlesRef.current = []
        // 重新开始自动发射烟花
        if (autoLaunch) {
          createInitialFireworks()
          startAutoFireworks()
        }
      } else {
        // 当页面不可见时，停止自动发射烟花
        if (autoFireworkIntervalRef.current) {
          clearInterval(autoFireworkIntervalRef.current)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [autoLaunch, createInitialFireworks, startAutoFireworks])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    if (autoLaunch && isVisible) {
      createInitialFireworks()
      startAutoFireworks()
    }

    const animate = () => {
      if (isTransparent) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      } else {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }

      // Limit the number of particles to prevent performance issues
      if (particlesRef.current.length > 1000) {
        particlesRef.current = particlesRef.current.slice(-1000)
      }

      for (let i = fireworksRef.current.length - 1; i >= 0; i--) {
        const firework = fireworksRef.current[i]
        firework.update()
        firework.draw(ctx)

        if (firework.done) {
          particlesRef.current.push(
            ...createEffectParticles(
              firework.x,
              firework.y,
              selectedColors,
              5,
              particleDensity,
              'burst',
              1,
            ),
          )
          fireworksRef.current.splice(i, 1)
        }
      }

      for (let i = particlesRef.current.length - 1; i >= 0; i--) {
        const particle = particlesRef.current[i]
        particle.update()
        particle.draw(ctx)

        if (particle.alpha <= 0) {
          particlesRef.current.splice(i, 1)
        }
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      if (autoFireworkIntervalRef.current) {
        clearInterval(autoFireworkIntervalRef.current)
      }
    }
  }, [
    autoLaunch,
    createInitialFireworks,
    startAutoFireworks,
    selectedColors,
    particleDensity,
    isVisible,
    isTransparent,
  ])

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || fireworksRef.current.length >= maxFireworks)
      return
    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    fireworksRef.current.push(
      createFirework(
        canvasRef.current.width / 2,
        canvasRef.current.height,
        x,
        y,
        selectedColors[Math.floor(Math.random() * selectedColors.length)],
      ),
    )
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || fireworksRef.current.length >= maxFireworks)
      return
    const rect = canvasRef.current.getBoundingClientRect()
    const touch = e.touches[0]
    const x = touch.clientX - rect.left
    const y = touch.clientY - rect.top
    fireworksRef.current.push(
      createFirework(
        canvasRef.current.width / 2,
        canvasRef.current.height,
        x,
        y,
        selectedColors[Math.floor(Math.random() * selectedColors.length)],
      ),
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>) => {
    if (
      (e.key === 'Enter' || e.key === ' ') &&
      fireworksRef.current.length < maxFireworks
    ) {
      if (!canvasRef.current) return
      fireworksRef.current.push(
        createRandomFirework(canvasRef.current, selectedColors),
      )
    }
  }

  return (
    <canvas
      ref={canvasRef}
      onClick={handleCanvasClick}
      onTouchStart={handleTouchStart}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  )
}

export const FireworkCanvas = forwardRef(FireworkCanvasComponent)
